import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, H3, InputLabel, InputWithUnit, Select, Switch, TextArea, TextInput } from '@components/Common';
import DateTimePicker from '@components/Common/DateTimePicker/DateTimePicker';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { Warning } from '@components/Common/SharedStyles';
import { SidebarButtons, SideForm } from '@components/Common/SideBarLayout';
import TankBalanceProgress from '@components/Common/TankBalance';
import { formatDateTime } from '@helpers/formatting';
import { toDateTimePickerString, nowIsoUtc } from '@helpers/dateUtils';
import { parsePositiveInteger } from '@helpers/parsing';
import { Product } from '@api/queries/products';
import { Tank } from '@api/queries/tanks';
import {
  Measurement,
  MeasurementCmd,
  useTankBalanceQuery,
  useTankSurfaceLevelQuery
} from '@api/queries/measurements';

const defaultMeasurement: MeasurementCmd = {
  id: null,
  affectsBalance: false,
  tankId: null,
  productId: null,
  notes: '',
  timestamp: nowIsoUtc(),
  value: null
};

interface Props {
  tank: Tank;
  products: Product[];
  measurement?: Measurement | null;
  isRemoving: boolean;
  isSaving: boolean;
  save: (cmd: MeasurementCmd) => void;
  close: () => void;
  remove: () => void;
}

const MeasurementEditor: React.FC<Props> = ({
  tank,
  products,
  measurement: measurementProp,
  isRemoving,
  isSaving,
  save,
  close,
  remove
}) => {
  const { t } = useTranslation();

  // Local form state
  const [measurement, setMeasurement] = useState<MeasurementCmd>(() =>
    measurementProp
      ? { ...measurementProp }
      : { ...defaultMeasurement, tankId: tank.id, productId: tank.productId }
  );

  const { tankId, productId, timestamp, id } = measurement;

  // TanStack Query for tank balance and surface level
  const { data: tankBalance } = useTankBalanceQuery(tankId, productId, timestamp, id);
  const { data: tankSurfaceLevel } = useTankSurfaceLevelQuery(tankId, timestamp);

  const { affectsBalance, notes, value } = measurement;
  const selectedProductId = tankBalance && tankBalance.product ? tankBalance.product.id : productId;

  const isEdit = measurementProp !== null && measurementProp !== undefined;
  const hasApprovedEntry = isEdit && measurementProp?.hasApprovedEntry;
  const isSaveDisabled = isSaving || hasApprovedEntry || !isValid();
  const isDoing = isSaving || isRemoving;

  const selectedTank = { value: tank.id, label: tank.name };
  const tanksOptions = [selectedTank];

  const enabledProducts = products.filter(p => p.id === selectedProductId);
  const productOptions = enabledProducts.map(i => ({ value: i.id, label: `${i.name} (${i.code})` }));
  const selectedProductOption = productOptions.find(i => i.value === selectedProductId) || null;

  function isValid() {
    return !!measurement.productId && !!measurement.timestamp && typeof measurement.value === 'number';
  }

  const handleValueChange = (newValue: string) => {
    const returnValue = parsePositiveInteger(newValue);
    handleChange(returnValue, 'value');
  };

  const handleSelectChange = (newValue: any, actionMeta: any, propName: string) => {
    const value = actionMeta.action === 'clear' ? null : newValue.value;
    handleChange(value, propName);
  };

  const handleChange = (newValue: any, propName: string) => {
    setMeasurement(prev => ({
      ...prev,
      [propName]: newValue
    }));
  };

  const onSubmit = (e: React.FormEvent<any>) => {
    e.preventDefault();
    save(measurement);
  };

  const onRemove = () => {
    if (confirm(t('measurements.confirm_delete'))) {
      remove();
    }
  };

  return (
    <SideForm onSubmit={onSubmit} onEscape={close}>
      {isEdit ? (
        <H3>
          {t('editing')}: <i>{formatDateTime(measurementProp?.timestamp, true)}</i>
        </H3>
      ) : (
        <H3>
          {t('add_new')} {t('measurements.measurement').toLowerCase()}
        </H3>
      )}
      {hasApprovedEntry && <Warning>{t('approval.warning_entry_approved')}</Warning>}
      <InputLabel htmlFor="tankId">{t('tank.tank')}</InputLabel>
      <Select
        name="tankId"
        isDisabled={true}
        isClearable={false}
        value={selectedTank}
        onChange={(i, j) => handleSelectChange(i, j, 'tankId')}
        options={tanksOptions}
      />
      {tankBalance && (
        <>
          <InputLabel>{t('measurements.tank_bookkeeping_balance')}</InputLabel>
          <TankBalanceProgress tankBalance={tankBalance} showUnit={true} showKilograms={true} />
        </>
      )}
      {tankSurfaceLevel && (
        <>
          <InputLabel>{t('measurements.tank_surface_radar_balance')}</InputLabel>
          <TankBalanceProgress tankBalance={tankSurfaceLevel} showUnit={true} showKilograms={true} />
        </>
      )}
      <InputLabel htmlFor="productId">{t('product.product')} *</InputLabel>
      <Select
        name="productId"
        isClearable={false}
        isDisabled={hasApprovedEntry}
        value={selectedProductOption}
        onChange={(i, j) => handleSelectChange(i, j, 'productId')}
        options={productOptions}
      />
      <DateTimePicker
        title={t('measurements.timestamp') + ' *'}
        name="timestamp"
        isDisabled={hasApprovedEntry}
        dateTime={toDateTimePickerString(timestamp, true)}
        hasTime={true}
        showTime={true}
        changed={newDateTime =>
          handleChange(
            newDateTime || measurement.timestamp,
            'timestamp'
          )
        }
      />
      <InputLabel htmlFor="value">{t('measurements.value')} *</InputLabel>
      <InputWithUnit>
        <TextInput
          id="value"
          name="value"
          type="text"
          autoFocus
          disabled={hasApprovedEntry}
          value={typeof value === 'number' ? value : ''}
          onChange={i => handleValueChange(i.target.value)}
        />
        <span>kg</span>
      </InputWithUnit>
      <Switch
        onChange={i => handleChange(i, 'affectsBalance')}
        checked={affectsBalance}
        disabled={hasApprovedEntry}
        id="affectsBalance"
        label={t('measurements.affects_balance')}
      />
      <InputLabel htmlFor="notes">{t('measurements.notes')}</InputLabel>
      <TextArea
        id="notes"
        name="notes"
        disabled={hasApprovedEntry}
        value={notes || ''}
        onChange={i => handleChange(i.target.value, i.target.name)}
      />
      <SidebarButtons>
        <Button type="button" secondary={true} disabled={isSaving} onClick={close}>
          {t('cancel')}
        </Button>
        <div />
        {isSaving && <OperationLoader title={t('saving') + '...'} />}
        {isEdit && (
          <Button type="button" danger={true} disabled={hasApprovedEntry || isDoing} onClick={onRemove}>
            {t('delete')}
          </Button>
        )}
        <Button type="submit" tertiary={true} disabled={isSaveDisabled}>
          {t('save')}
        </Button>
      </SidebarButtons>
    </SideForm>
  );
};

export default MeasurementEditor;
